import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { GlassCard } from './GlassCard';
import { LucideIcon } from 'lucide-react';

interface BenefitCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  color: string;
  delay?: number;
  index?: number;
}

const BenefitCardComponent: React.FC<BenefitCardProps> = ({
  icon,
  title,
  description,
  color,
  delay = 0,
  index = 0
}) => {
  // Color mapping to ensure all Tailwind classes are included
  const colorConfig = {
    blue: {
      iconBg: 'from-blue-500/20 to-blue-600/30',
      iconBorder: 'border-blue-400/30',
      iconText: 'text-blue-400',
      iconTextHover: 'text-blue-300',
      hoverBg: 'from-blue-600/5 to-blue-400/10',
      glowBg: 'from-blue-400/20 to-blue-600/30'
    },
    emerald: {
      iconBg: 'from-emerald-500/20 to-emerald-600/30',
      iconBorder: 'border-emerald-400/30',
      iconText: 'text-emerald-400',
      iconTextHover: 'text-emerald-300',
      hoverBg: 'from-emerald-600/5 to-emerald-400/10',
      glowBg: 'from-emerald-400/20 to-emerald-600/30'
    },
    cyan: {
      iconBg: 'from-cyan-500/20 to-cyan-600/30',
      iconBorder: 'border-cyan-400/30',
      iconText: 'text-cyan-400',
      iconTextHover: 'text-cyan-300',
      hoverBg: 'from-cyan-600/5 to-cyan-400/10',
      glowBg: 'from-cyan-400/20 to-cyan-600/30'
    },
    purple: {
      iconBg: 'from-purple-500/20 to-purple-600/30',
      iconBorder: 'border-purple-400/30',
      iconText: 'text-purple-400',
      iconTextHover: 'text-purple-300',
      hoverBg: 'from-purple-600/5 to-purple-400/10',
      glowBg: 'from-purple-400/20 to-purple-600/30'
    },
    orange: {
      iconBg: 'from-orange-500/20 to-orange-600/30',
      iconBorder: 'border-orange-400/30',
      iconText: 'text-orange-400',
      iconTextHover: 'text-orange-300',
      hoverBg: 'from-orange-600/5 to-orange-400/10',
      glowBg: 'from-orange-400/20 to-orange-600/30'
    },
    green: {
      iconBg: 'from-green-500/20 to-green-600/30',
      iconBorder: 'border-green-400/30',
      iconText: 'text-green-400',
      iconTextHover: 'text-green-300',
      hoverBg: 'from-green-600/5 to-green-400/10',
      glowBg: 'from-green-400/20 to-green-600/30'
    }
  };

  const colorClasses = colorConfig[color as keyof typeof colorConfig] || colorConfig.blue;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ 
        duration: 0.8, 
        delay: delay || index * 0.1,
        type: "spring",
        stiffness: 80
      }}
      className="group h-full"
    >
      <motion.div
        whileHover={{ 
          y: -4,
          scale: 1.01
        }}
        transition={{ 
          type: "spring", 
          stiffness: 400, 
          damping: 25,
          mass: 0.5
        }}
        style={{ 
          transformStyle: "preserve-3d",
          transformOrigin: "center center",
          willChange: 'transform',
          transform: 'translateZ(0)'
        }}
        className="h-full"
      >
        <GlassCard className="p-8 rounded-2xl h-full relative overflow-hidden border border-white/10 backdrop-blur-sm">
          {/* Layered background with depth */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800/30 via-gray-900/20 to-black/30" />
          <div className={`absolute inset-0 bg-gradient-to-tr ${colorClasses.hoverBg} opacity-0 group-hover:opacity-100 transition-opacity duration-700`} />
          
          {/* Main content with z-depth */}
          <div className="relative z-10" style={{ transform: "translateZ(10px)" }}>
            {/* Enhanced icon section */}
            <motion.div 
              className={`w-16 h-16 mb-6 bg-gradient-to-br ${colorClasses.iconBg} rounded-2xl border ${colorClasses.iconBorder} flex items-center justify-center relative`}
              whileHover={{ 
                scale: 1.05
              }}
              transition={{ 
                duration: 0.2,
                ease: "easeOut"
              }}
              style={{
                willChange: 'transform'
              }}
            >
              {/* Consistent icon shadow/glow for all icons */}
              <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses.glowBg} rounded-2xl blur-md group-hover:blur-lg transition-all duration-500`} />
              <div className={`${colorClasses.iconText} relative z-10`}>
                {React.createElement(icon, { className: 'w-8 h-8' })}
              </div>
            </motion.div>
            
            <motion.h3 
              className="text-xl font-bold text-white mb-4 group-hover:text-gray-100 transition-colors duration-300"
              style={{ transform: "translateZ(5px)" }}
            >
              {title}
            </motion.h3>
            
            <motion.p 
              className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed"
              style={{ transform: "translateZ(2px)" }}
            >
              {description}
            </motion.p>
          </div>
          
          {/* Subtle depth indicators */}
          <div className="absolute bottom-4 right-4 w-1 h-8 bg-gradient-to-t from-transparent via-white/5 to-white/10 rounded-full" />
          <div className="absolute top-4 left-4 w-8 h-1 bg-gradient-to-r from-transparent via-white/5 to-white/10 rounded-full" />
        </GlassCard>
      </motion.div>
    </motion.div>
  );
};

export const BenefitCard = memo(BenefitCardComponent);
